import { useEffect } from 'react'
import { ChevronLeft, ChevronRight, Presentation, X } from 'lucide-react'
import { presentationApi } from '../../services/api'
import { useLiveEventStore } from '../../stores/liveEventStore'
import { BACKEND_URL } from '../../services/config'

// Synchronized slide viewer. Everyone sees the host-controlled slide;
// only the host gets the prev/next controls and the "stop for everyone"
// action. OSPROD-272: the X used to render for the host only, so a
// non-host viewer (web) had NO way to leave the fullscreen overlay. Now
// EVERY viewer gets a top-right X and backdrop-click — both close the
// overlay for the LOCAL viewer only (a "Reopen" chip brings it back);
// the run itself stays ACTIVE for the room. Ending the presentation for
// everyone remains host-only (server-enforced), via the host's Stop.
//
// OSPROD-272 round 2 — QA still saw no X in the browser while backdrop
// click worked. Not a permission or platform difference: the controls
// were anchored to the SLIDE (`relative max-w-5xl`), and the slide is
// rendered at the PDF's natural aspect ratio with no height cap. A
// portrait page at max-w-5xl is ~1448px tall against ~800px of usable
// height; `items-center` splits the overflow, so the top ~320px — the
// corner holding the X — was clipped by the parent's `overflow-hidden`.
// The backdrop's side margins stayed clickable, hence "backdrop yes, X
// no". Fix: controls anchor to the backdrop, and the slide is bounded by
// the viewport.
export default function PresentationOverlay({ channelId, isHost }: { channelId: string; isHost: boolean }) {
  const pres = useLiveEventStore((s) => s.presentation)
  const dismissedRunId = useLiveEventStore((s) => s.presentationDismissedRunId)
  const dismissPresentation = useLiveEventStore((s) => s.dismissPresentation)

  const runId = pres?.run.id ?? null
  const overlayVisible = !!pres && !!pres.presentation && dismissedRunId !== runId

  // Escape closes locally — the usual escape hatch for a fullscreen layer,
  // and a second way out if the corner controls are ever unreachable again.
  useEffect(() => {
    if (!overlayVisible || !runId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissPresentation(runId)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [overlayVisible, runId, dismissPresentation])

  if (!pres || !pres.presentation) return null
  const idx = pres.run.currentSlideIndex
  const slide = pres.slides.find((s) => s.index === idx)
  const total = pres.presentation.slideCount

  const go = (next: number) => {
    if (next < 0 || next >= total) return
    presentationApi.patchRun(channelId, pres.run.id, { currentSlideIndex: next }).catch(() => {})
  }
  // Host: stop the run server-side — ends the presentation for EVERYONE.
  const stop = () => presentationApi.patchRun(channelId, pres.run.id, { state: 'CLOSED' }).catch(() => {})
  // Anyone: hide the overlay locally; the synchronized run keeps going.
  const closeLocal = () => dismissPresentation(pres.run.id)

  // Locally dismissed while the run is still active → compact floating
  // chip instead of the fullscreen overlay, so the viewer can get back in.
  // Sits above the bottom control bar: the old `top-20 right-4` collided
  // pixel-for-pixel with RaisedHandsPanel. The host also gets Stop here —
  // otherwise the only way to end a presentation is to re-open it first.
  if (dismissedRunId === pres.run.id) {
    return (
      <div className="absolute bottom-24 right-4 z-40 flex items-center gap-1 rounded-full bg-card ring-1 ring-border shadow-lg">
        <button
          onClick={() => dismissPresentation(null)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-foreground hover:bg-muted transition"
          title="Reopen the presentation"
        >
          <Presentation size={14} className="text-primary" />
          Presentation in progress · Reopen
        </button>
        {isHost && (
          <button
            onClick={stop}
            className="pr-3 pl-1 py-1.5 text-xs text-muted-foreground hover:text-destructive transition"
            title="Stop presentation for everyone"
          >
            Stop
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 p-6"
      // Backdrop click closes for the LOCAL viewer only. Guarded on
      // currentTarget so clicks on the slide / controls don't dismiss.
      onClick={(e) => { if (e.target === e.currentTarget) closeLocal() }}
    >
      {/* Anchored to the backdrop, NOT the slide — a tall slide must never
          be able to push these off-screen (that was the OSPROD-272 retest
          failure). */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
        <span className="text-xs text-white/80 bg-black/50 rounded px-2 py-1">
          {idx + 1} / {total}
        </span>
        {isHost && (
          <button
            onClick={stop}
            className="flex items-center gap-1 bg-black/50 hover:bg-black/70 rounded px-2 py-1 transition"
            title="Stop presentation for everyone"
          >
            <span className="text-xs text-white/80">Stop</span>
            <X size={14} className="text-white" />
          </button>
        )}
        {/* Local close for EVERY viewer (host included) — hides the overlay
            on this client only. */}
        <button
          onClick={closeLocal}
          className="bg-black/50 hover:bg-black/70 rounded p-1 transition"
          title="Close (just for you — the presentation keeps running)"
        >
          <X size={16} className="text-white" />
        </button>
      </div>

      {isHost && (
        <>
          <button
            onClick={() => go(idx - 1)}
            disabled={idx === 0}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 rounded-full p-2 disabled:opacity-30"
          >
            <ChevronLeft className="text-white" />
          </button>
          <button
            onClick={() => go(idx + 1)}
            disabled={idx >= total - 1}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 rounded-full p-2 disabled:opacity-30"
          >
            <ChevronRight className="text-white" />
          </button>
        </>
      )}

      {slide && (
        <img
          src={`${BACKEND_URL}/uploads/${slide.imageKey}`}
          alt={`slide ${idx + 1}`}
          // max-h/object-contain: portrait PDFs are taller than the viewport
          // at full width, and letting them overflow is what clipped the
          // controls. Contain keeps the whole page visible instead.
          className="max-w-5xl max-h-full w-auto h-auto object-contain rounded-lg shadow-lg"
        />
      )}
    </div>
  )
}
